import { DesignSchemeSet } from '@data/buildingProjects/DesignSchemeSet';
import FVector from '@data/geometry/FVector';
import UnobstructedBlock from '@data/Simulations/pipeline/blocks/UnobstructedBlock';
import { Pipeline, PipelineExecutionResult } from '@data/Simulations/pipeline/Pipeline';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { buildCloseToDesignSchemesBlockForSet } from '@rendering/DesignSchemes/DesignSchemeUtils';
import { DesignSchemeRenderer } from '@rendering/DesignSchemes/DesignSchemeRenderer';
import { FloorBasedRenderer } from '@rendering/DesignSchemes/FloorBased/FloorBasedRenderer';
import {
    DesignSchemeSetType,
    FacadeType,
    FloorBasedDesignState,
    LayerKind,
    NotificationType,
    PipelinePhase,
    SchemeKey,
    UnobstructedMode,
} from '@visualizer/types';
import Grid from 'Geodata/grid';
import Layer from 'Simulations/Layer';
import { SensorData } from 'Simulations/SensorPoint';
import useNotificationStore from '@gui/store/notification';
import { getGrid } from 'store/LoadingStore';
import { DesignScheme } from '../designScheme';
import { computeInitialFloorBasedHeightFit } from './FloorBasedInitialFit';

/**
 * DesignScheme subclass implementing the floor-based extrusion logic.
 * Syncs its renderer configuration with Zustand for dynamic architectural control.
 */
export class FloorBased extends DesignScheme {
    private pendingInitialHeightFit = false;
    private mainLayermaxHeight: number | null = null;
    private hasShownFullSiteCloseToDsWarning = false;

    constructor(
        footprint: FVector[],
        footprintId: string,
        set: DesignSchemeSet,
        config: FloorBasedDesignState,
        linkedLayers?: Map<LayerKind, Layer>
    ) {
        super(footprint, footprintId, set, config, linkedLayers);

        // 1. Link the FACADE/COMPONENT layer
        if (!this.linkedLayers.has(LayerKind.FACADE)) this.linkedLayers.set(LayerKind.FACADE, getGrid().getActiveLayer());
        if (!this.linkedLayers.has(LayerKind.COMPONENT)) this.linkedLayers.set(LayerKind.COMPONENT, getGrid().getActiveLayer());

        // 2. Create the FACADE/COMPONENT pipelines (same processing chain)
        const facadePipeline = new Pipeline();
        facadePipeline.addBlock(
            new UnobstructedBlock({
                unobstructedMode: UnobstructedMode.Corners,
                isValue: false,
            })
        );
        const componentPipeline = new Pipeline();
        componentPipeline.addBlock(
            new UnobstructedBlock({
                unobstructedMode: UnobstructedMode.Corners,
                isValue: false,
            })
        );

        // 3. Register the pipelines inside the parent system
        this.updatePipeline(LayerKind.FACADE, facadePipeline);
        this.updatePipeline(LayerKind.COMPONENT, componentPipeline);

        // 4. Initialize FACADE sensors immediately
        const facadeLayer = this.linkedLayers.get(LayerKind.FACADE)!;
        const facadeResult = facadeLayer.runPipeline({
            suppressNotifications: true,
            capturePhase: PipelinePhase.Regular,
            octTree: getGrid().octTree!,
        });
        this.updateSensors(facadeResult, facadeLayer.getID());

        // 5. Initialize COMPONENT sensors immediately (using the same pipeline as FACADE, but different input layer):
        const componentLayer = this.linkedLayers.get(LayerKind.COMPONENT)!;
        if (componentLayer.getID() !== facadeLayer.getID()) {
            const componentResult = componentLayer.runPipeline({
                suppressNotifications: true,
                capturePhase: PipelinePhase.Regular,
                octTree: getGrid().octTree!,
            });
            this.updateSensors(componentResult, componentLayer.getID());
        }

        // Initialize visibility and update state:
        if (config.active) this.update();
    }

    /**
     * Schedules the initial height fitting for this newly created scheme and tries to
     * apply it immediately if MAIN sensors are already available.
     */
    public fitBuildingHeightToSensors(): void {
        this.pendingInitialHeightFit = true;
        this.tryApplyInitialBuildingHeight();
    }

    // =====================================
    // IMPLEMENTED ABSTRACT PROPERTIES
    // =====================================

    // Renderer Factory & Config hooks:
    protected override createRenderer(config: FloorBasedDesignState): DesignSchemeRenderer {
        return new FloorBasedRenderer(this, config);
    }

    override getRendererKey() {
        return SchemeKey.FLOOR_BASED;
    }

    // =====================================
    // PIPELINE & SENSORS
    // =====================================

    getFacadeSensorDatas(): SensorData[] {
        return this.getSensorDatasFor(LayerKind.FACADE);
    }

    getFacadeMinMax(): [number, number] {
        return this.getMinMaxFor(LayerKind.FACADE);
    }

    public getMainLayermaxHeight(): number | null {
        return this.mainLayermaxHeight;
    }

    public getComponentSensorsForLayer(layerId: string): SensorData[] {
        const layer = getGrid().searchLayerByUID(layerId);
        if (!layer) return [];

        const layerResult = layer.runPipeline({
            suppressNotifications: true,
            capturePhase: PipelinePhase.Regular,
            octTree: getGrid().octTree!,
        });
        const baseResult = this.runPipeline(layerResult.data, layerResult.outputMinMax);
        return this.computeSensorResult(LayerKind.COMPONENT, layerResult, baseResult).data;
    }

    public override updateSensors(pipelineResult: PipelineExecutionResult, layerId: string): void {
        const kinds = this.getKindsForLayerId(layerId);
        if (kinds.length === 0) return;

        // Pre-Pass: recompute maxHeight.
        if (kinds.includes(LayerKind.MAIN)) {
            let maxHeight = Number.NEGATIVE_INFINITY;
            for (const sensor of pipelineResult.data) {
                const z = sensor.getPosition().z;
                if (z > maxHeight) maxHeight = z;
            }
            this.mainLayermaxHeight = Number.isFinite(maxHeight) ? maxHeight : null;
        }

        // Pass 1: run the base DesignScheme pipeline once to get the sensors that define the
        // tmp FloorBased volume (MAIN still uses the standard InsidePolygon flow):
        const baseResult = this.runPipeline(pipelineResult.data, pipelineResult.outputMinMax);

        for (const kind of kinds) {
            let finalResult = baseResult;

            if (kind === LayerKind.FACADE || kind === LayerKind.COMPONENT) {
                // Pass 2: FACADE/COMPONENT intentionally diverge from MAIN.
                // They use CloseToDS (opaque shell) -> Unobstructed.
                finalResult = this.computeSensorResult(kind, pipelineResult, baseResult);
            } else if (kind !== LayerKind.MAIN) {
                const extraPipeline = this.pipelineByLayerKind.get(kind);
                if (extraPipeline) {
                    extraPipeline.getBlocks().forEach((block) => block.clearCache());
                    finalResult = extraPipeline.execute(baseResult.data, baseResult.outputMinMax, { octTree: getGrid().octTree! });
                }
            }

            this.sensorsByLayerKind.set(kind, {
                data: finalResult.data,
                minMax: finalResult.outputMinMax,
            });
        }

        // Extra Pass: Once the sensors are available, apply height fit:
        if (this.tryApplyInitialBuildingHeight()) return;

        this.update();
    }

    // Changes Floors heights to fit the MAIN sensors:
    private tryApplyInitialBuildingHeight(): boolean {
        // 1. Check if the height fit is required:
        if (!this.pendingInitialHeightFit) return false;

        const sensors = this.getSensorDatasFor(LayerKind.MAIN);
        if (sensors.length === 0) return false;

        // 2. Now that sensors are available, set pendingFit to false and fit:
        this.pendingInitialHeightFit = false;

        const renderer = this.getRenderer() as FloorBasedRenderer;
        const currentConfig = renderer.getConfig() as FloorBasedDesignState;
        const heightPatch = computeInitialFloorBasedHeightFit(currentConfig, sensors);
        if (!heightPatch) return false;

        renderer.updateConfig(heightPatch);
        ControllerManager.getInstance()
            .getDesignSchemeController()
            .updateDesignScheme(this.getDesignSchemeSet().getID(), this.getFootprintId(), this.getID(), {
                floorBased: heightPatch,
            });
        this.update();
        return true;
    }

    private computeSensorResult(
        kind: LayerKind.FACADE | LayerKind.COMPONENT,
        layerResult: PipelineExecutionResult,
        baseResult: PipelineExecutionResult
    ): PipelineExecutionResult {
        const renderer = this.getRenderer();
        const floorBasedRenderer = renderer instanceof FloorBasedRenderer ? renderer : undefined;
        const kindPipeline = this.pipelineByLayerKind.get(kind);
        if (!floorBasedRenderer || !kindPipeline || baseResult.data.length === 0) {
            return {
                ...layerResult,
                data: [],
                outputMinMax: layerResult.outputMinMax,
            };
        }

        // CloseToDS (Not on Fullsite):
        const bypassCloseToDs = this.getDesignSchemeSet().getType() === DesignSchemeSetType.FULL_SITE;
        let filteredResult = baseResult;
        if (bypassCloseToDs) {
            if (!this.hasShownFullSiteCloseToDsWarning) {
                useNotificationStore
                    .getState()
                    .showNotification(
                        'In Full Site, facade/component proximity filtering can be unreliable because sensor points may be missing near analysis-area boundaries and after facade-facing filtering.',
                        NotificationType.Warning
                    );
                this.hasShownFullSiteCloseToDsWarning = true;
            }
        } else {
            // 1) Build temporary FloorBased shell (using MAIN sensors only) and run the CloseToDS Pipeline on it:
            const shellGroup = floorBasedRenderer.buildFacadeShellGroup(baseResult.data);

            // 2) Create a tmp pipeline (avoid add + move + remove of the block):
            const closeToDsPipeline = new Pipeline();
            closeToDsPipeline.addBlock(buildCloseToDesignSchemesBlockForSet([this], [shellGroup], Grid.gridRes));
            filteredResult = closeToDsPipeline.execute(layerResult.data, layerResult.outputMinMax, { octTree: getGrid().octTree! });
        }

        const currentConfig = floorBasedRenderer.getConfig() as FloorBasedDesignState;
        // "Facade with Openings" is based on edge-assigned facade sensors, not vector-direction unobstructed filtering.
        // Hourly Sun layers can have near-zero vector sums and would otherwise collapse to fully opaque facades.
        if (kind === LayerKind.COMPONENT) {
            // Components stay coupled to the same proximity-filtered source used for this set.
            return filteredResult;
        }

        if (kind === LayerKind.FACADE && currentConfig.facadeState.type === FacadeType.FACADE_WITH_OPENINGS) {
            return filteredResult;
        }

        // 3) Run facadePipeline which runs the UnobstructedBlock:
        kindPipeline.getBlocks().forEach((block) => block.clearCache());
        return kindPipeline.execute(filteredResult.data, filteredResult.outputMinMax, { octTree: getGrid().octTree! });
    }
}
